import { useCallback, useState } from 'react';
import { AxiosInstance } from 'axios';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';

import { Dashboard } from 'types/dashboard';
import {
  ChartType,
  DataWidget,
  isDataWidget,
  isDataWidgetCounter,
  isDataWidgetLeaderboardBar,
  isDataWidgetLeaderboardTable,
  isDataWidgetPie,
  isDataWidgetTable,
  isDataWidgetTimeseriesBar,
  isDataWidgetTimeseriesBarGrouped,
  isDataWidgetTimeseriesBarStacked,
  isDataWidgetTimeseriesHeatmap,
  isDataWidgetTimeseriesLine,
} from 'types/dashboard_widget';
import { FilterInput } from 'dashboards/types/filter';
import { TimeRangeInput, TimeSeriesResponse } from 'dashboards/types/time';

import { HEATMAP_CHART_GRANULARITIES } from 'dashboards/services/heatmap';
import { resolveGranularity } from 'dashboards/services/granularity';

import { downloadFile } from 'services/download_file';
import { rowsToTimezone } from 'dashboards/services/table';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useAPI } from 'hooks/api/useAPI';

/**
 * Converts an ISO timestamp to the specified timezone and formats it for display
 */
function formatTimestampForTimezone(isoTimestamp: string, timezone: string): string {
  const dt = DateTime.fromISO(isoTimestamp).setZone(timezone);
  return dt.isValid ? dt.toFormat('yyyy-MM-dd HH:mm') : isoTimestamp;
}

/**
 * Formats the time range for the filename suffix
 * Format: from_YYYY-MM-DD_HH-mm_to_YYYY-MM-DD_HH-mm
 */
function formatTimeRangeForFilename(
  timeRange: TimeRangeInput,
  timezone: string
): string {
  const startDt = DateTime.fromISO(timeRange.start).setZone(timezone);
  const stopDt = DateTime.fromISO(timeRange.stop).setZone(timezone);

  const startFormatted = startDt.isValid
    ? startDt.toFormat('yyyy-MM-dd_HH-mm')
    : 'unknown';
  const stopFormatted = stopDt.isValid
    ? stopDt.toFormat('yyyy-MM-dd_HH-mm')
    : 'unknown';

  return `${startFormatted}_to_${stopFormatted}`;
}

type ExportParams = {
  dashboard: Dashboard;
  timeRange: TimeRangeInput;
  timezone: string;
  gatewayId?: string;
  tags?: string[];
};

type SheetData = {
  name: string;
  headers: string[];
  rows: string[][];
};

/**
 * Fetches time series data from the API
 */
async function fetchTimeSeries(
  client: AxiosInstance,
  applicationID: string,
  params: {
    metricName: string;
    timeRange: TimeRangeInput;
    timeZone: string;
    timeGranularity: string;
    filters: FilterInput[];
    groupBy?: string[];
  }
): Promise<TimeSeriesResponse> {
  const { data } = await client.post<TimeSeriesResponse>(
    `/apps/${applicationID}/user_metrics/timeseries`,
    params
  );
  return data;
}

/**
 * Fetches leaderboard data from the API
 */
async function fetchLeaderboard(
  client: AxiosInstance,
  applicationID: string,
  params: {
    metricName: string;
    timeRange: TimeRangeInput;
    timeZone: string;
    dimensions: string[];
    rowLimit: number;
    sort: string;
    filters: FilterInput[];
  }
): Promise<{ headers: string[]; rows: string[][] }> {
  const { data } = await client.post(
    `/apps/${applicationID}/user_metrics/leaderboard`,
    params
  );
  return data;
}

/**
 * Fetches data grid data from the API
 */
async function fetchDataGrid(
  client: AxiosInstance,
  applicationID: string,
  params: {
    table: string;
    timeRange: TimeRangeInput;
    timeZone: string;
    columns: string[];
    sort: string;
    orderByColumn: number;
    filters?: FilterInput[];
    first?: number;
  }
): Promise<{ headers: string[]; rows: string[][] }> {
  const { data } = await client.post(
    `/apps/${applicationID}/user_metrics/datagrid`,
    { first: 1000, ...params } // Fetch more rows for export
  );
  return data;
}

/**
 * Fetches counter data from the API
 */
async function fetchCounter(
  client: AxiosInstance,
  applicationID: string,
  params: {
    metricName: string;
    timeRange: TimeRangeInput;
    timeZone: string;
    filters: FilterInput[];
  }
): Promise<{ value: string }> {
  const { data } = await client.post(
    `/apps/${applicationID}/user_metrics/counter`,
    params
  );
  return data;
}

/**
 * Adds gateway filter to the filters array if gatewayId is provided
 */
function addTopLevelFilters(
  filters: FilterInput[],
  gatewayId?: string,
  tags?: string[]
): FilterInput[] {
  const result = [...filters];

  if (gatewayId) {
    result.push({
      column: 'gateway_id',
      operator: 'EQUALS',
      value: gatewayId,
    });
  }

  if (tags && tags.length > 0) {
    // Add tag filters - assuming tags filter by metrics_tag column
    tags.forEach((tag) => {
      result.push({
        column: 'metrics_tag',
        operator: 'EQUALS',
        value: tag,
      });
    });
  }

  return result;
}

/**
 * Exports a timeseries widget (bar or line) to sheet data
 * Format: label in first column, time values as columns (transposed)
 */
async function exportTimeSeriesWidget(
  client: AxiosInstance,
  applicationID: string,
  widget: DataWidget<'timeseries-bar'> | DataWidget<'timeseries-line'>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  const { metric, granularity: rawGranularity } = widget.query;
  const granularity = resolveGranularity(rawGranularity, timeRange);
  const filters = addTopLevelFilters(
    sanitizeDataWidgetFilters(widget.query.filters),
    gatewayId,
    tags
  );

  try {
    const data = await fetchTimeSeries(client, applicationID, {
      metricName: metric,
      timeRange,
      timeZone: timezone,
      timeGranularity: granularity,
      filters,
    });

    if (!data || data.length === 0) {
      return null;
    }

    const [{ labels, values }] = data;

    // Convert timestamps to timezone for column headers
    const formattedTimestamps = labels.map((ts) =>
      formatTimestampForTimezone(ts, timezone)
    );

    // Transposed format: label in first column, time values as columns
    // Single row for simple timeseries (metric name as label)
    return {
      name: widget.name,
      headers: ['label', ...formattedTimestamps],
      rows: [[metric, ...values.map((v) => String(v))]],
    };
  } catch {
    return null;
  }
}

/**
 * Exports a stacked/grouped bar chart widget to sheet data
 * Format: label in first column, time values as columns (transposed)
 */
async function exportStackedBarWidget(
  client: AxiosInstance,
  applicationID: string,
  widget:
    | DataWidget<'timeseries-bar-stacked'>
    | DataWidget<'timeseries-bar-grouped'>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  const { metric, granularity: rawGranularity, dimensions } = widget.query;
  const granularity = resolveGranularity(rawGranularity, timeRange);
  const filters = addTopLevelFilters(
    sanitizeDataWidgetFilters(widget.query.filters),
    gatewayId,
    tags
  );

  try {
    const data = await fetchTimeSeries(client, applicationID, {
      metricName: metric,
      timeRange,
      timeZone: timezone,
      timeGranularity: granularity,
      filters,
      groupBy: dimensions.map(({ columnName }) => columnName),
    });

    if (!data || data.length === 0) {
      return null;
    }

    const [{ labels }] = data;

    // Convert timestamps to timezone for column headers
    const formattedTimestamps = labels.map((ts) =>
      formatTimestampForTimezone(ts, timezone)
    );

    // Extract series labels (dimension values)
    const seriesLabels = data.map(
      ({ labels: _labels, values: _values, ...headers }) =>
        String(Object.values(headers)[0])
    );

    // Transposed format: label in first column, time values as columns
    // Each series becomes a row
    return {
      name: widget.name,
      headers: ['label', ...formattedTimestamps],
      rows: seriesLabels.map((label, seriesIndex) => [
        label,
        ...data[seriesIndex].values.map((v) => String(v ?? 0)),
      ]),
    };
  } catch {
    return null;
  }
}

/**
 * Exports a heatmap widget to sheet data
 * Format: label in first column, time values as columns
 */
async function exportHeatmapWidget(
  client: AxiosInstance,
  applicationID: string,
  widget: DataWidget<'timeseries-heatmap'>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  const { metric, granularity: rawGranularity, dimensions } = widget.query;
  const granularity = resolveGranularity(
    rawGranularity,
    timeRange,
    HEATMAP_CHART_GRANULARITIES
  );
  const filters = addTopLevelFilters(
    sanitizeDataWidgetFilters(widget.query.filters),
    gatewayId,
    tags
  );

  try {
    const data = await fetchTimeSeries(client, applicationID, {
      metricName: metric,
      timeRange,
      timeZone: timezone,
      timeGranularity: granularity,
      filters,
      groupBy: dimensions.map(({ columnName }) => columnName),
    });

    if (!data || data.length === 0) {
      return null;
    }

    const [{ labels }] = data;

    // Convert timestamps to timezone for column headers
    const formattedTimestamps = labels.map((ts) =>
      formatTimestampForTimezone(ts, timezone)
    );

    // Extract series labels (dimension values)
    const seriesLabels = data.map(
      ({ labels: _labels, values: _values, ...headers }) =>
        String(Object.values(headers)[0])
    );

    // Label in first column, time values as columns
    return {
      name: widget.name,
      headers: ['label', ...formattedTimestamps],
      rows: seriesLabels.map((label, index) => [
        label,
        ...data[index].values.map((v) => String(v ?? 0)),
      ]),
    };
  } catch {
    return null;
  }
}

/**
 * Exports a leaderboard widget (bar or table) to sheet data
 */
async function exportLeaderboardWidget(
  client: AxiosInstance,
  applicationID: string,
  widget: DataWidget<'leaderboard-bar'> | DataWidget<'leaderboard-table'>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  const { metric, dimensions, rowLimit, sort } = widget.query;
  const filters = addTopLevelFilters(
    sanitizeDataWidgetFilters(widget.query.filters),
    gatewayId,
    tags
  );

  try {
    const data = await fetchLeaderboard(client, applicationID, {
      metricName: metric,
      timeRange,
      timeZone: timezone,
      dimensions: dimensions.map(({ columnName }) => columnName),
      rowLimit,
      sort,
      filters,
    });

    if (!data || data.rows.length === 0) {
      return null;
    }

    const csv = rowsToTimezone({
      headers: data.headers,
      rows: data.rows,
      timezone,
    });

    return {
      name: widget.name,
      headers: csv.headers,
      rows: csv.rows,
    };
  } catch {
    return null;
  }
}

/**
 * Exports a pie chart widget to sheet data
 */
async function exportPieWidget(
  client: AxiosInstance,
  applicationID: string,
  widget: DataWidget<'pie'>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  // Pie chart uses same API as leaderboard
  return exportLeaderboardWidget(
    client,
    applicationID,
    widget as unknown as DataWidget<'leaderboard-bar'>,
    timeRange,
    timezone,
    gatewayId,
    tags
  );
}

/**
 * Exports a table widget to sheet data
 */
async function exportTableWidget(
  client: AxiosInstance,
  applicationID: string,
  widget: DataWidget<'table'>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  const { dataPool, columns, orderByColumn, sort } = widget.query;
  const filters = addTopLevelFilters(
    sanitizeDataWidgetFilters(widget.query.filters),
    gatewayId,
    tags
  );

  const columnIndex = columns.indexOf(orderByColumn);
  const orderByColumnIndex =
    orderByColumn === 'timestamp' || columnIndex < 0 ? 1 : columnIndex + 1;

  try {
    const data = await fetchDataGrid(client, applicationID, {
      table: dataPool.name,
      timeRange,
      timeZone: timezone,
      columns,
      sort,
      orderByColumn: orderByColumnIndex,
      filters,
    });

    if (!data || data.rows.length === 0) {
      return null;
    }

    const csv = rowsToTimezone({
      headers: data.headers,
      rows: data.rows,
      timezone,
    });

    return {
      name: widget.name,
      headers: csv.headers,
      rows: csv.rows,
    };
  } catch {
    return null;
  }
}

/**
 * Exports a counter widget to sheet data
 */
async function exportCounterWidget(
  client: AxiosInstance,
  applicationID: string,
  widget: DataWidget<'counter'>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  const { metric } = widget.query;
  const filters = addTopLevelFilters(
    sanitizeDataWidgetFilters(widget.query.filters),
    gatewayId,
    tags
  );

  try {
    const data = await fetchCounter(client, applicationID, {
      metricName: metric,
      timeRange,
      timeZone: timezone,
      filters,
    });

    return {
      name: widget.name,
      headers: ['metric', 'value'],
      rows: [[metric, data.value]],
    };
  } catch {
    return null;
  }
}

/**
 * Routes widget export to the appropriate handler based on chart type
 */
async function exportDataWidget(
  client: AxiosInstance,
  applicationID: string,
  widget: DataWidget<ChartType>,
  timeRange: TimeRangeInput,
  timezone: string,
  gatewayId?: string,
  tags?: string[]
): Promise<SheetData | null> {
  if (
    isDataWidgetTimeseriesBar(widget) ||
    isDataWidgetTimeseriesLine(widget)
  ) {
    return exportTimeSeriesWidget(
      client,
      applicationID,
      widget,
      timeRange,
      timezone,
      gatewayId,
      tags
    );
  }

  if (
    isDataWidgetTimeseriesBarStacked(widget) ||
    isDataWidgetTimeseriesBarGrouped(widget)
  ) {
    return exportStackedBarWidget(
      client,
      applicationID,
      widget,
      timeRange,
      timezone,
      gatewayId,
      tags
    );
  }

  if (isDataWidgetTimeseriesHeatmap(widget)) {
    return exportHeatmapWidget(
      client,
      applicationID,
      widget,
      timeRange,
      timezone,
      gatewayId,
      tags
    );
  }

  if (
    isDataWidgetLeaderboardBar(widget) ||
    isDataWidgetLeaderboardTable(widget)
  ) {
    return exportLeaderboardWidget(
      client,
      applicationID,
      widget,
      timeRange,
      timezone,
      gatewayId,
      tags
    );
  }

  if (isDataWidgetPie(widget)) {
    return exportPieWidget(
      client,
      applicationID,
      widget,
      timeRange,
      timezone,
      gatewayId,
      tags
    );
  }

  if (isDataWidgetTable(widget)) {
    return exportTableWidget(
      client,
      applicationID,
      widget,
      timeRange,
      timezone,
      gatewayId,
      tags
    );
  }

  if (isDataWidgetCounter(widget)) {
    return exportCounterWidget(
      client,
      applicationID,
      widget,
      timeRange,
      timezone,
      gatewayId,
      tags
    );
  }

  return null;
}

/**
 * Sanitizes a sheet name for Excel compatibility
 * Excel sheet names must be <= 31 chars and cannot contain: \ / ? * [ ]
 */
function sanitizeSheetName(name: string, index: number): string {
  const sanitized = name
    .replace(/[\\/?*[\]]/g, '_')
    .slice(0, 28); // Leave room for potential suffix

  // Ensure unique names by appending index if needed
  return `${sanitized}_${index + 1}`;
}

/**
 * Creates an Excel workbook with multiple sheets from the sheet data
 */
function createExcelFile(sheets: SheetData[]): Blob {
  const workbook = XLSX.utils.book_new();

  sheets.forEach((sheet, index) => {
    const worksheetData = [sheet.headers, ...sheet.rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const sheetName = sanitizeSheetName(sheet.name, index);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Hook for exporting all data widgets from a dashboard as an Excel file
 */
export function useExportDashboardData() {
  const { client, applicationID } = useAPI();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportDashboard = useCallback(
    async ({ dashboard, timeRange, timezone, gatewayId, tags }: ExportParams) => {
      if (!applicationID) {
        setError('Application ID is required');
        return;
      }

      setIsExporting(true);
      setError(null);

      try {
        // Filter to only data widgets
        const dataWidgets = dashboard.configuration.widgets.filter(isDataWidget);

        if (dataWidgets.length === 0) {
          setError('No data widgets to export');
          setIsExporting(false);
          return;
        }

        // Fetch data for all widgets in parallel
        const sheetPromises = dataWidgets.map((widget) =>
          exportDataWidget(
            client,
            applicationID,
            widget,
            timeRange,
            timezone,
            gatewayId,
            tags
          )
        );

        const sheets = await Promise.all(sheetPromises);

        // Filter out null results (failed or empty widgets)
        const validSheets = sheets.filter(
          (sheet): sheet is SheetData => sheet !== null
        );

        if (validSheets.length === 0) {
          setError('No data available to export');
          setIsExporting(false);
          return;
        }

        // Create and download the Excel file
        const excelBlob = createExcelFile(validSheets);
        const timeRangeSuffix = formatTimeRangeForFilename(timeRange, timezone);
        downloadFile(`${dashboard.name}_${timeRangeSuffix}.xlsx`, excelBlob);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to export dashboard data'
        );
      } finally {
        setIsExporting(false);
      }
    },
    [client, applicationID]
  );

  return {
    exportDashboard,
    isExporting,
    error,
  };
}
